import { useEffect, useState, type ReactNode } from "react";
import { ActionButton, GlassPanel, UtilityButton } from "@guandan/ui";
import {
  createFriendClient,
  type FriendClient,
  type FriendRequestView,
  type FriendState,
  type FriendView,
} from "../lib/friends";
import { BottomNav, type LobbyRoute } from "./Lobby";

const EMPTY_STATE: FriendState = { friends: [], incoming: [], outgoing: [] };

export function FriendsScreen({
  offline,
  onBack,
  onNavigate,
}: {
  readonly offline: boolean;
  readonly onBack: () => void;
  readonly onNavigate: (route: LobbyRoute) => void;
}) {
  const [client] = useState<FriendClient | null>(() => createFriendClient());
  const [state, setState] = useState<FriendState>(EMPTY_STATE);
  const [targetNickname, setTargetNickname] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const onlineActions = !offline && client !== null;

  useEffect(() => {
    if (!onlineActions) {
      setStatus(
        offline
          ? "当前离线：好友数据不会伪造或写入本机，联网后可重新同步。"
          : "当前构建未连接好友服务，联网好友操作已停用。",
      );
      return;
    }
    let active = true;
    setStatus("正在同步好友…");
    client
      .list()
      .then((next) => {
        if (!active) return;
        setState(next);
        setStatus("好友状态已同步");
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(messageOf(cause));
        setStatus("好友同步失败");
      });
    return () => {
      active = false;
    };
  }, [client, offline, onlineActions]);

  async function execute(
    action: (service: FriendClient) => Promise<FriendState>,
  ): Promise<boolean> {
    if (!onlineActions) return false;
    setBusy(true);
    setError("");
    try {
      const next = await action(client);
      setState(next);
      setStatus("好友状态已同步");
      return true;
    } catch (cause) {
      setError(messageOf(cause));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function submitRequest() {
    if (await execute((service) => service.request(targetNickname))) {
      setTargetNickname("");
    }
  }

  const noRelations =
    state.friends.length === 0 &&
    state.incoming.length === 0 &&
    state.outgoing.length === 0;

  return (
    <main className="sub-screen friends-screen">
      <header className="sub-header">
        <div>
          <span>真实账号关系</span>
          <h1>好友</h1>
        </div>
        <UtilityButton onClick={onBack}>返回大厅</UtilityButton>
      </header>
      <GlassPanel className="feature-panel friends-panel">
        <p className="local-service-note" role="status">{status}</p>
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitRequest();
          }}
        >
          <label className="field">
            <span>好友昵称</span>
            <input
              type="text"
              autoComplete="off"
              placeholder="输入对方的唯一昵称"
              value={targetNickname}
              disabled={!onlineActions || busy}
              onChange={(event) => setTargetNickname(event.target.value)}
            />
          </label>
          <ActionButton
            tone="orange"
            disabled={!onlineActions || busy || targetNickname.trim().length < 2}
            type="submit"
          >
            发送申请
          </ActionButton>
        </form>
        <div className="friends-toolbar">
          <span>只会匹配已完成注册的精确昵称，不展示陌生人目录。</span>
          <ActionButton
            tone="ghost"
            size="sm"
            disabled={!onlineActions || busy}
            onClick={() => void execute((service) => service.list())}
          >
            刷新
          </ActionButton>
        </div>
        {error ? <p className="form-error" role="alert">{error}</p> : null}

        {state.incoming.length > 0 ? (
          <FriendSection title={`收到的申请（${state.incoming.length}）`}>
            {state.incoming.map((request) => (
              <RequestRow
                key={request.requestId}
                request={request}
                direction="incoming"
                disabled={!onlineActions || busy}
                onAccept={() => void execute((service) => service.accept(request.requestId))}
                onReject={() => void execute((service) => service.reject(request.requestId))}
              />
            ))}
          </FriendSection>
        ) : null}

        {state.outgoing.length > 0 ? (
          <FriendSection title={`等待对方处理（${state.outgoing.length}）`}>
            {state.outgoing.map((request) => (
              <RequestRow
                key={request.requestId}
                request={request}
                direction="outgoing"
                disabled
              />
            ))}
          </FriendSection>
        ) : null}

        {state.friends.length > 0 ? (
          <FriendSection title={`我的好友（${state.friends.length}）`}>
            {state.friends.map((friend) => (
              <FriendRow
                key={friend.friendshipId}
                friend={friend}
                disabled={!onlineActions || busy}
                onRemove={() => void execute((service) => service.remove(friend.userId))}
              />
            ))}
          </FriendSection>
        ) : null}

        {noRelations ? (
          <div className="empty-state">
            <h2>暂无好友关系</h2>
            <p>输入对方完成注册时使用的昵称发送申请；对方接受后才会出现在好友列表。</p>
          </div>
        ) : null}
      </GlassPanel>
      <BottomNav active="friends" onNavigate={onNavigate} />
    </main>
  );
}

function FriendSection({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="friend-section">
      <h2>{title}</h2>
      <div className="friend-list">{children}</div>
    </section>
  );
}

function FriendRow({
  friend,
  disabled,
  onRemove,
}: {
  readonly friend: FriendView;
  readonly disabled: boolean;
  readonly onRemove: () => void;
}) {
  return (
    <article>
      <span aria-hidden="true">{friend.nickname.slice(0, 1)}</span>
      <div>
        <strong>{friend.nickname}</strong>
        <small>唯一昵称账号</small>
      </div>
      <ActionButton tone="ghost" size="sm" disabled={disabled} onClick={onRemove}>
        删除好友
      </ActionButton>
    </article>
  );
}

function RequestRow({
  request,
  direction,
  disabled,
  onAccept,
  onReject,
}: {
  readonly request: FriendRequestView;
  readonly direction: "incoming" | "outgoing";
  readonly disabled: boolean;
  readonly onAccept?: () => void;
  readonly onReject?: () => void;
}) {
  return (
    <article>
      <span aria-hidden="true">{request.nickname.slice(0, 1)}</span>
      <div>
        <strong>{request.nickname}</strong>
        <small>昵称好友申请</small>
      </div>
      {direction === "incoming" ? (
        <div className="friend-actions">
          <ActionButton tone="jade" size="sm" disabled={disabled} onClick={onAccept}>
            接受
          </ActionButton>
          <ActionButton tone="ghost" size="sm" disabled={disabled} onClick={onReject}>
            拒绝
          </ActionButton>
        </div>
      ) : (
        <small className="friend-pending">等待接受</small>
      )}
    </article>
  );
}

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : "好友操作失败";
}
